import { COLORS, FONT, TYPOGRAPHY, Z_INDEX } from '@alto/design-library-tokens';
import React from 'react';
import { Platform } from 'react-native';
import styled from 'styled-components/native';
import { useAccessibilityFocus } from '../../../hooks/useAccessibilityFocus';
import { useScreenSize } from '../../../utils';
import { Text, type TextProps } from './Text';

export type H1Props = Pick<TextProps, 'center' | 'children'> & {
  readonly autofocus?: boolean;
  readonly underline?: boolean;
  readonly zIndex?: number;
  readonly testID?: string;
};

const StyledH1 = styled(Text)<
  H1Props & { isMDScreenOrBigger?: boolean; underline?: boolean; zIndex?: number; color?: string; font?: string }
>`
  ${({ isMDScreenOrBigger }) => {
    if (Platform.OS === 'web' && isMDScreenOrBigger) {
      return `
        font-size: ${TYPOGRAPHY.TEXT.HEADER.LG.px};
        line-height: ${TYPOGRAPHY.TEXT.HEADER.LG.value * TYPOGRAPHY.LINE_HEIGHT.MD}px;
      `;
    }
    return `
      font-size: ${TYPOGRAPHY.TEXT.HEADER.MD.px};
      line-height: ${TYPOGRAPHY.TEXT.HEADER.MD.value * TYPOGRAPHY.LINE_HEIGHT.MD}px;
    `;
  }}
  letter-spacing: ${TYPOGRAPHY.LETTER_SPACING.SM.px};
  color: ${({ color }) => color};
  font-family: ${({ font }) => font};
  ${({ zIndex }) => {
    if (zIndex) {
      return `z-index: ${zIndex};`;
    }
    return `z-index: ${Z_INDEX.Z_DEFAULT};`;
  }}
`;

export const H1 = ({ center, children, autofocus, zIndex, underline, testID }: H1Props) => {
  const { isMDScreenOrBigger } = useScreenSize();
  const ref = useAccessibilityFocus(autofocus);

  return (
    <StyledH1
      color={COLORS.TEXT_COLORS.PRIMARY}
      font={FONT.HEADER.SEMIBOLD}
      textDecorationLine={underline ? 'underline' : undefined}
      accessible={autofocus}
      focusable
      role={'header' as any}
      aria-level={1}
      center={center}
      ref={ref}
      isMDScreenOrBigger={isMDScreenOrBigger}
      testID={testID}
      zIndex={zIndex}
    >
      {children}
    </StyledH1>
  );
};
